import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faExternalLinkAlt,
  faGlobe,
  faCheckCircle,
  faChessPawn,
  faChessKnight
} from '@fortawesome/free-solid-svg-icons';
import {
  faTwitter,
  faYoutube,
  faInstagram,
  faTiktok,
  faTwitch,
  faApple,
  faSpotify,
  faFacebookF,
  faLinkedinIn,
  faGithub,
  faDiscord
} from '@fortawesome/free-brands-svg-icons';
import { getWidgetBySlug, getWidgets } from '../services/widgetService';
import TitleBadge from '../components/TitleBadge';

const SOCIAL_ICONS = {
  twitter: { icon: faTwitter, color: 'hover:text-blue-400', label: 'Twitter' },
  youtube: { icon: faYoutube, color: 'hover:text-red-500', label: 'YouTube' },
  instagram: { icon: faInstagram, color: 'hover:text-pink-400', label: 'Instagram' },
  tiktok: { icon: faTiktok, color: 'hover:text-gray-100', label: 'TikTok' },
  twitch: { icon: faTwitch, color: 'hover:text-purple-500', label: 'Twitch' },
  website: { icon: faGlobe, color: 'hover:text-yellow-400', label: 'Website' },
  apple: { icon: faApple, color: 'hover:text-gray-100', label: 'Apple' },
  spotify: { icon: faSpotify, color: 'hover:text-green-400', label: 'Spotify' },
  facebook: { icon: faFacebookF, color: 'hover:text-blue-600', label: 'Facebook' },
  linkedin: { icon: faLinkedinIn, color: 'hover:text-blue-500', label: 'LinkedIn' },
  github: { icon: faGithub, color: 'hover:text-gray-100', label: 'GitHub' },
  discord: { icon: faDiscord, color: 'hover:text-indigo-400', label: 'Discord' },
  chessCom: { icon: faChessPawn, color: 'hover:text-green-500', label: 'Chess.com' },
  chesscom: { icon: faChessPawn, color: 'hover:text-green-500', label: 'Chess.com' },
  lichess: { icon: faChessKnight, color: 'hover:text-gray-100', label: 'Lichess' },
};

const WidgetDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [widget, setWidget] = useState(null);
  const [relatedWidgets, setRelatedWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchWidget = async () => {
      setLoading(true);
      try {
        const w = await getWidgetBySlug(slug);
        if (!w) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setWidget(w);

        // Fetch related widgets from same category
        const primaryTag = Array.isArray(w.tag) ? w.tag[0] : w.tag;
        if (primaryTag) {
          const all = await getWidgets();
          const related = all
            .filter(rw =>
              rw.id !== w.id &&
              !rw.hidden &&
              !rw.excludeFromAll &&
              (Array.isArray(rw.tag) ? rw.tag.includes(primaryTag) : rw.tag === primaryTag)
            )
            .slice(0, 8);
          setRelatedWidgets(related);
        }
      } catch (err) {
        console.error('Error fetching widget:', err);
        setNotFound(true);
      }
      setLoading(false);
    };
    fetchWidget();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400 text-lg">Loading...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="text-gray-400 text-lg">Page not found</div>
        <button
          onClick={() => navigate('/')}
          className="text-blue-400 hover:text-blue-300 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Directory
        </button>
      </div>
    );
  }

  const VALID_TAG_ROUTES = ['creators', 'play', 'learn', 'analysis', 'events'];
  const TAG_DISPLAY = { creators: 'people', play: 'play', learn: 'learn', analysis: 'analysis', events: 'events' };
  const TAG_ROUTE = { creators: '/people', play: '/play', learn: '/learn', analysis: '/analysis', events: '/events' };
  const rawTags = Array.isArray(widget.tag) ? widget.tag : (widget.tag ? [widget.tag] : []);
  const allTags = rawTags.filter(t => VALID_TAG_ROUTES.includes(t));
  const primaryTag = allTags[0] || null;
  const primaryTagLabel = primaryTag ? (TAG_DISPLAY[primaryTag] || primaryTag) : null;
  const primaryTagRoute = primaryTag ? (TAG_ROUTE[primaryTag] || `/${primaryTag}`) : null;
  const description = widget.subtitle || widget.description || `Discover ${widget.title} on TrendingChess.com`;

  return (
    <>
      <Helmet>
        <title>{widget.title} | TrendingChess.com</title>
        <meta name="description" content={description.substring(0, 160)} />
        <meta property="og:title" content={`${widget.title} | TrendingChess.com`} />
        <meta property="og:description" content={description.substring(0, 160)} />
        {widget.icon && <meta property="og:image" content={widget.icon} />}
        <meta property="og:url" content={`https://trendingchess.com/${slug}`} />
        <link rel="canonical" href={`https://trendingchess.com/${slug}`} />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-4xl mx-auto px-4 py-8"
      >
        {/* Breadcrumb */}
        <nav className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-8">
          <Link to="/" className="hover:text-gray-200 transition-colors">TrendingChess</Link>
          <span>&rsaquo;</span>
          {primaryTag && (
            <>
              <Link to={primaryTagRoute} className="hover:text-gray-200 transition-colors capitalize">
                {primaryTagLabel}
              </Link>
              <span>&rsaquo;</span>
            </>
          )}
          <span className="text-gray-200">{widget.title}</span>
        </nav>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex flex-col md:flex-row items-start gap-6 mb-10"
        >
          {/* Icon */}
          {widget.icon && (
            <div className="flex-shrink-0">
              {typeof widget.icon === 'string' && (widget.icon.startsWith('http') || widget.icon.startsWith('/')) ? (
                <img
                  src={widget.icon}
                  alt={widget.title}
                  className="w-24 h-24 rounded-2xl object-cover border-2 border-gray-700"
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-gray-800 border-2 border-gray-700 flex items-center justify-center text-4xl">
                  {widget.icon}
                </div>
              )}
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {widget.titled && widget.chessTitle && (
                <span className="inline-flex items-center px-2 py-1 bg-red-600 text-white text-sm font-bold rounded">
                  {widget.chessTitle}
                </span>
              )}
              <h1 className="text-3xl md:text-4xl font-bold text-gray-100">
                {widget.title}
              </h1>
              {widget.verified && (
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-xl" title="Verified" />
              )}
              {widget.isNew && (
                <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">NEW</span>
              )}
            </div>

            {widget.subtitle && (
              <p className="text-lg text-gray-400 mb-4">{widget.subtitle}</p>
            )}

            {/* Social links — merge widget.links with platform detected from widget.link */}
            {(() => {
              const merged = { ...(widget.links || {}) };
              // Auto-detect platform from the main link and inject if not already in links
              if (widget.link) {
                try {
                  const host = new URL(widget.link).hostname.replace(/^www\./, '');
                  const platformMap = {
                    'twitter.com': 'twitter', 'x.com': 'twitter',
                    'youtube.com': 'youtube', 'youtu.be': 'youtube',
                    'instagram.com': 'instagram',
                    'tiktok.com': 'tiktok',
                    'twitch.tv': 'twitch',
                    'podcasts.apple.com': 'apple', 'apps.apple.com': 'apple',
                    'open.spotify.com': 'spotify', 'spotify.com': 'spotify',
                    'facebook.com': 'facebook', 'fb.com': 'facebook',
                    'linkedin.com': 'linkedin',
                    'github.com': 'github',
                    'discord.gg': 'discord', 'discord.com': 'discord',
                    'chess.com': 'chessCom',
                    'lichess.org': 'lichess',
                  };
                  // Match against known domains (check if host ends with the key)
                  const detected = Object.entries(platformMap).find(([domain]) => host === domain || host.endsWith('.' + domain));
                  if (detected && !merged[detected[1]]) {
                    merged[detected[1]] = widget.link;
                  } else if (!detected && !merged.website) {
                    // Unknown platform — show as globe icon
                    merged.website = widget.link;
                  }
                } catch {}
              }
              const entries = Object.entries(merged).filter(([, url]) => url);
              if (entries.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-3">
                  {entries.map(([platform, url]) => {
                    const config = SOCIAL_ICONS[platform];
                    if (!config) return null;
                    const urls = Array.isArray(url) ? url : [url];
                    return urls.map((u, i) => (
                      <a
                        key={`${platform}-${i}`}
                        href={u}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-gray-400 ${config.color} transition-colors text-lg`}
                        title={config.label}
                      >
                        <FontAwesomeIcon icon={config.icon} />
                      </a>
                    ));
                  })}
                </div>
              );
            })()}
          </div>

          {/* Visit button */}
          {widget.link && (
            <a
              href={widget.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold rounded-xl transition-colors"
            >
              Visit {(() => { try { const h = new URL(widget.link).hostname.replace(/^www\./, ''); return h.includes('.') ? h : widget.link; } catch { return widget.link; } })()}
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-sm" />
            </a>
          )}
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-gray-800 border border-gray-700 rounded-xl p-8 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-100">About {widget.title}</h2>
            {allTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <Link
                    key={tag}
                    to={`/${tag}`}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-full text-sm text-gray-300 hover:border-gray-400 transition-colors capitalize"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {widget.longDescription ? (
            <div
              className="text-gray-300 text-lg leading-relaxed prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: widget.longDescription }}
            />
          ) : widget.description ? (
            <p className="text-gray-300 text-lg leading-relaxed">{widget.description}</p>
          ) : (
            <p className="text-gray-400 text-lg">
              {widget.title} is listed on TrendingChess.com, the chess ecosystem directory.
            </p>
          )}
        </motion.div>

        {/* Features */}
        {widget.features && widget.features.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="bg-gray-800 border border-gray-700 rounded-xl p-8 mb-8"
          >
            <h2 className="text-2xl font-semibold text-gray-100 mb-6">Key Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {widget.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Related Listings */}
        {relatedWidgets.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <h2 className="text-2xl font-semibold text-gray-100 mb-6">
              Related {primaryTagLabel ? <span className="capitalize">{primaryTagLabel}</span> : 'Listings'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedWidgets.filter(rw => rw.title !== 'Add Listing').map(rw => (
                <Link
                  key={rw.id}
                  to={rw.slug ? `/${rw.slug}` : '#'}
                  onClick={(e) => {
                    if (!rw.slug && rw.link) {
                      e.preventDefault();
                      window.open(rw.link, '_blank');
                    }
                  }}
                  className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-gray-500 transition-colors flex flex-col items-center text-center gap-3"
                >
                  {rw.icon && typeof rw.icon === 'string' && (rw.icon.startsWith('http') || rw.icon.startsWith('/')) ? (
                    <img src={rw.icon} alt={rw.title} className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-2xl">
                      {rw.icon || '□'}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-bold text-gray-100"><TitleBadge titled={rw.titled} chessTitle={rw.chessTitle} size="sm" />{rw.title}</div>
                    {rw.subtitle && (
                      <div className="text-xs text-gray-400 mt-1 line-clamp-2">{rw.subtitle}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* Return + Footer */}
        <div className="mt-12 mb-8 flex flex-col items-center gap-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 border border-gray-700 hover:border-gray-500 text-gray-200 font-semibold rounded-xl transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
            Return to TrendingChess.com
          </Link>
          <p className="text-xs text-gray-500 text-center max-w-md">
            Want to submit a new listing, suggest an update, or just say hello? Reach out on our <Link to="/contact" className="text-gray-400 hover:text-gray-300 underline">contact page</Link>.
          </p>
        </div>
      </motion.div>
    </>
  );
};

export default WidgetDetailPage;
